/**
 * Les personnes qu'on peut désigner sur un appel d'offres (chargé de réponse, responsable du
 * marché, responsable d'une pièce ou d'une demande de l'acheteur) — jugées sur l'ENTITÉ DE L'AO
 * (décisions PO n° 66 et 68).
 *
 * ## Pourquoi pas `useCollaborateursDesignables`
 *
 * Celui-là lit l'annuaire (`/admin/users`, collaborateurs ACTIFS) : il sert les rôles du
 * CLIENT (commercial, contact administratif), qui ne dépendent d'aucun AO. Le serveur juge le
 * chargé et le responsable du marché sur l'entité de l'AO : un collaborateur joignable —
 * invité compris — qui voit les AO de cette entité, de là ou d'un parent. Depuis le siège,
 * l'annuaire proposait pour l'AO d'une agence des collègues qui ne pouvaient pas l'ouvrir,
 * puis le serveur les refusait. `GET /tenders/designables` lit le MÊME prédicat que le refus
 * (`_designable_sur_l_entite`) : l'écran ne propose que ce que le serveur accepte.
 *
 * ## L'entité jugée
 *
 * `tenderId` donné : celle de CET AO (fiche, carte « Notre équipe », réaffectation d'une
 * fiche, modale d'une pièce de checklist, demandes de l'acheteur). `null` : celle de la session — la réaffectation en MASSE, dont l'aperçu écarte à
 * la ligne une fiche que la personne choisie ne voit pas.
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { parNom, type PersonneDesignable } from '@/components/shared/ReassignRolesFields.helpers'
import { TENDERS_ENDPOINT } from '@/hooks/useTenders.types'
import { apiFetch } from '@/lib/api'
import { handleApiError } from '@/lib/api-error'
import { tendersKeys } from '@/lib/queryKeys.tenders'

interface DesignablesResponse {
    items: Array<{ id: string; name: string }>
    total: number
}

/** L'URL de la route — une seule écriture, lue aussi par les témoins. */
export function urlDesDesignables(entityId: string, tenderId: string | null): string {
    const parametres = new URLSearchParams({ entity_id: entityId })
    if (tenderId) parametres.set('tender_id', tenderId)
    return `${TENDERS_ENDPOINT}/tenders/designables?${parametres.toString()}`
}

export function useDesignablesSurLaConsultation({
    entityId,
    tenderId,
    enabled = true,
}: {
    entityId: string
    tenderId: string | null
    enabled?: boolean
}): {
    personnes: PersonneDesignable[]
    nonListes: number
    isLoading: boolean
    error: unknown
} {
    const { i18n } = useTranslation()
    const requete = useQuery<DesignablesResponse>({
        queryKey: tendersKeys.tenders.designables(entityId, tenderId),
        queryFn: async () => {
            const res = await apiFetch(urlDesDesignables(entityId, tenderId))
            if (!res.ok) await handleApiError(res)
            return res.json() as Promise<DesignablesResponse>
        },
        // Sans entité de session (`/auth/me` pas encore lu), la route refuserait la requête.
        enabled: enabled && Boolean(entityId),
        staleTime: 60_000,
    })
    const items = requete.data?.items
    const personnes = useMemo(
        () => parNom((items ?? []).map((p) => ({ id: p.id, nom: p.name })), i18n.language),
        [items, i18n.language],
    )
    return {
        personnes,
        nonListes: Math.max(0, (requete.data?.total ?? 0) - (items?.length ?? 0)),
        isLoading: requete.isLoading,
        error: requete.error,
    }
}
